<!--
  ConfirmDialog.vue
  Centralized confirmation dialog component. Mounted once in App.vue,
  it reads state from the useConfirmDialog composable and resolves a
  promise when the user confirms or cancels.

  Supports an optional v-alert inside the card body for warnings
  (e.g., "This action cannot be undone.").
-->
<template>
  <!-- Single shared confirmation dialog, max 400px wide -->
  <v-dialog :model-value="show" @update:model-value="onDialogChange" max-width="400">
    <v-card>
      <!-- Dialog title -->
      <v-card-title>{{ title }}</v-card-title>

      <v-card-text>
        <!-- Main confirmation text -->
        {{ text }}
        <!-- Optional warning/info alert (used by UsersManagement for destructive actions) -->
        <v-alert
          v-if="alert"
          :type="alert.type"
          variant="tonal"
          class="mt-3"
          density="compact"
        >
          {{ alert.text }}
        </v-alert>
      </v-card-text>

      <!-- Action buttons: Cancel (text) and Confirm (colored) -->
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="handleCancel">Cancel</v-btn>
        <v-btn :color="confirmColor" variant="flat" @click="handleConfirm">
          {{ confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
/**
 * ConfirmDialog.vue - Script Section
 *
 * Reads all state from the useConfirmDialog composable singleton.
 * Resolves the pending promise via handleConfirm/handleCancel.
 * The onDialogChange handler ensures dismissing via backdrop/escape
 * also resolves the promise as a cancel.
 */

import { useConfirmDialog } from "@/composables/useConfirmDialog.js";

const {
  show,           // Dialog visibility
  title,          // Dialog title
  text,           // Dialog body text
  confirmText,    // Confirm button label
  confirmColor,   // Confirm button color
  alert,          // Optional v-alert config { type, text }
  handleConfirm,  // Resolves promise with true
  handleCancel,   // Resolves promise with false
} = useConfirmDialog();

/**
 * onDialogChange - Handles v-dialog model updates (e.g., backdrop click, Escape key).
 * When the dialog closes externally, treat it as a cancel.
 */
const onDialogChange = (val) => {
  if (!val) {
    handleCancel();
  }
};
</script>
